import type { CSSProperties } from 'react';
import { cn } from 'utils/styles';
import KeyLine from 'components/shared/KeyLine';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { CompareGraphProps } from './types';

/**
 * Graph variant of the Compare Table.
 * Renders a horizontal bar graph. The first variable is the "leading" bar:
 * it sets the full container width and uses the accent color; every other
 * bar scales to the leading variable's percentage.
 */
const CompareGraph = ({ description, variables }: CompareGraphProps) => {
  if (!variables.length) return null;

  const leadingPercentage = variables[0].percentage || 0;

  return (
    <div className="flex flex-col gap-6 md:gap-8">
      <KeyLine />
      {description && (
        <div className="text-body-saans">
          <RichTextCopy copy={description} ySpacing="none" />
        </div>
      )}
      <ul className="flex flex-col gap-4 md:gap-8">
        {variables.map((variable, index) => {
          const isLeading = index === 0;
          const widthRatio = leadingPercentage > 0 ? Math.min(variable.percentage / leadingPercentage, 1) : 0;

          return (
            <li key={variable.sys?.id ?? index} className="flex flex-col gap-2 md:flex-row md:items-center md:gap-6">
              {/*
                Bar width scales by `--bar-ratio` (leading bar is 1):
                - Mobile: leading bar is full width (name below)
                - Desktop: leading bar is full width minus 224px (≈200px space for name + 24px gap)
              */}
              <div
                data-testid="compare-graph-bar"
                className={cn(
                  'relative flex h-12 shrink-0 items-center rounded-[4px] md:rounded-[8px]',
                  'min-w-[65px] w-[calc(var(--bar-ratio)*100%)] md:min-w-[85px] md:w-[calc((100%_-_224px)*var(--bar-ratio))]',
                  isLeading ? 'bg-highlight' : 'bg-kale',
                )}
                style={{ '--bar-ratio': widthRatio } as CSSProperties}>
                <span
                  className={cn(
                    'ml-auto whitespace-nowrap pr-6 text-legal font-medium md:text-label-saans md:font-semibold',
                    isLeading ? 'text-reserve' : 'text-content-inverse',
                  )}>
                  {variable.percentage}%
                </span>
              </div>
              <div className="text-left text-label-saans font-semibold md:min-w-0 md:flex-1">
                <RichTextCopy copy={variable.name} ySpacing="none" inline />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CompareGraph;
